import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key } from '@shared/api/query-keys';

import type { UserId } from './types';

/**
 * Block and unblock — the one write **both** account surfaces issue, which is
 * why it lives here rather than on either of them. A page importing the other
 * page's hook would reach across a boundary the shape of this feature exists to
 * keep; `_shared/` is the sub-surface the two routes legitimately share.
 *
 * **Neither auto-retries and neither is optimistic.** Blocking revokes every
 * live session in the same transaction, and a row that repaints as blocked
 * before the server agreed is a state the client must not pretend to know.
 *
 * **No `confirm=true` on either.** That flag is the wire half of a *hard*
 * removal, and a block removes nothing: the entitlements, the lists, and the
 * cases all survive it, and the account returns whole the moment the block is
 * lifted. Sending the flag where the contract does not declare it would document
 * a guard that does not exist. The confirmation dialog still runs in front of
 * the block — it is the operator's guard, not the wire's.
 *
 * **Blocking is idempotent server-side**, so a double press is a `200` no-op
 * rather than a second revocation; unblocking does **not** restore the revoked
 * sessions, and the dialog's copy says the account signs in again rather than
 * promising it resumes where it stopped.
 *
 * The invalidation set carries the users report: a block moves a figure on the
 * console entry, and a dashboard still showing the old count is drift the
 * operator has no way to notice.
 */
function useAccountStandingInvalidation() {
  const queryClient = useQueryClient();

  return (userId: UserId) => {
    void queryClient.invalidateQueries({ queryKey: key('auth', 'adminUsers') });
    void queryClient.invalidateQueries({ queryKey: key('auth', 'adminUser', userId) });
    void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'users') });
  };
}

export function useBlockUser() {
  const invalidate = useAccountStandingInvalidation();

  return useMutation({
    mutationFn: (userId: UserId) => api.post<null>('auth', `/admin/users/${userId}:block`),
    onSuccess: (_data, userId) => invalidate(userId),
    retry: false,
  });
}

export function useUnblockUser() {
  const invalidate = useAccountStandingInvalidation();

  return useMutation({
    mutationFn: (userId: UserId) => api.post<null>('auth', `/admin/users/${userId}:unblock`),
    onSuccess: (_data, userId) => invalidate(userId),
    retry: false,
  });
}
